(() => {
  'use strict';
  const sliders = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 750px)');

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-kr-video-trigger]');
    if (!trigger || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const root = trigger.closest('.kr-youtube');
    if (!root || root.dataset.loaded === 'true') return;
    event.preventDefault();
    root.dataset.loaded = 'true';
    const frame = document.createElement('iframe');
    frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(root.dataset.videoId) + '?autoplay=1&rel=0';
    frame.title = root.dataset.videoTitle || 'Видео';
    frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    frame.allowFullscreen = true;
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.className = 'kr-youtube__frame';
    trigger.replaceWith(frame);
    frame.focus();
  });

  const initSlider = (root) => {
    if (sliders.has(root)) return;
    const track = root.querySelector('[data-slider-track]');
    const cards = Array.from(root.querySelectorAll('[data-video-item]'));
    if (!track || !cards.length) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const previous = root.querySelector('[data-slider-prev]');
    const next = root.querySelector('[data-slider-next]');
    let visible = false;
    let frame = 0;
    let generation = 0;
    let active = null;
    const on = (target, type, listener, options = {}) => target?.addEventListener(type, listener, { ...options, signal });
    const state = (card, playing) => {
      card.querySelector('.kr-video-slider__card')?.classList.toggle('is-playing', playing);
      const control = card.querySelector('[data-video-control]');
      if (control) control.setAttribute('aria-label', playing ? control.dataset.pauseLabel : control.dataset.playLabel);
    };
    const pauseAll = () => {
      generation++;
      active = null;
      cards.forEach((card) => {
        const video = card.querySelector('[data-video]');
        if (video && !video.paused) video.pause();
        state(card, false);
      });
    };
    const hydrate = (video) => {
      if (video.dataset.hydrated === 'true') return;
      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      const mp4 = sources.filter((source) => source.type === 'video/mp4').sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
      const target = Math.min(720, Math.max(360, video.clientWidth * Math.min(window.devicePixelRatio || 1, 2)));
      const chosen = mp4.find((source) => Number(source.dataset.width) >= target) || mp4[mp4.length - 1] || sources[0];
      if (!chosen) return;
      video.preload = 'none';
      video.src = chosen.dataset.src;
      video.dataset.hydrated = 'true';
      video.load();
    };
    const play = async (card) => {
      const video = card.querySelector('[data-video]');
      if (!video || active === card) return;
      pauseAll();
      const token = generation;
      active = card;
      hydrate(video);
      video.muted = true;
      try {
        await video.play();
        if (token !== generation || document.hidden) video.pause();
      } catch (_) {
        if (active === card) active = null;
        state(card, false);
      }
    };
    const automatic = () => {
      if (!visible || document.hidden || !desktop.matches || reducedMotion.matches || navigator.connection?.saveData || root.dataset.autoplayDesktop !== 'true' || active) return;
      const bounds = track.getBoundingClientRect();
      const card = cards.find((item) => {
        const box = item.getBoundingClientRect();
        return Math.min(box.right, bounds.right) - Math.max(box.left, bounds.left) > box.width * 0.5;
      });
      if (card) play(card);
    };
    const updateArrows = () => {
      const end = Math.max(0, track.scrollWidth - track.clientWidth);
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= end - 2;
    };
    cards.forEach((card) => {
      const video = card.querySelector('[data-video]');
      const control = card.querySelector('[data-video-control]');
      if (!video || !control) return;
      on(control, 'click', () => {
        if (active === card) pauseAll();
        else play(card);
      });
      on(video, 'playing', () => state(card, true));
      on(video, 'pause', () => { state(card, false); if (active === card) active = null; });
      on(video, 'ended', () => { state(card, false); if (active === card) active = null; });
      const progress = card.querySelector('[data-progress]');
      on(video, 'timeupdate', () => {
        if (progress && video.duration > 0) progress.style.strokeDashoffset = String(100 - Math.min(100, video.currentTime / video.duration * 100));
      });
    });
    const scroll = (direction) => {
      const width = cards[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      track.scrollBy({ left: direction * 2 * (width + gap), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    on(previous, 'click', () => scroll(-1));
    on(next, 'click', () => scroll(1));
    on(track, 'scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; updateArrows(); });
    }, { passive: true });
    on(track, 'scrollend', automatic);
    on(document, 'visibilitychange', () => { if (document.hidden) pauseAll(); });
    let start = null;
    on(track, 'pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.target.closest('button, a')) return;
      start = { x: event.clientX, left: track.scrollLeft };
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    });
    on(track, 'pointermove', (event) => { if (start) track.scrollLeft = start.left - (event.clientX - start.x); });
    const endDrag = (event) => {
      start = null;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    };
    on(track, 'pointerup', endDrag);
    on(track, 'pointercancel', endDrag);
    let sectionObserver;
    let cardObserver;
    if ('IntersectionObserver' in window) {
      sectionObserver = new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) automatic(); else pauseAll();
      }, { threshold: 0.15 });
      sectionObserver.observe(root);
      cardObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (!entry.isIntersecting && active === entry.target) pauseAll(); });
      }, { root: track, threshold: 0.2 });
      cards.forEach((card) => cardObserver.observe(card));
    }
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(updateArrows) : null;
    if (resizeObserver) resizeObserver.observe(track);
    else on(window, 'resize', updateArrows, { passive: true });
    updateArrows();
    sliders.set(root, () => {
      pauseAll();
      controller.abort();
      sectionObserver?.disconnect();
      cardObserver?.disconnect();
      resizeObserver?.disconnect();
      cancelAnimationFrame(frame);
      sliders.delete(root);
    });
  };
  const init = (scope = document) => scope.querySelectorAll('.kr-video-slider').forEach(initSlider);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('.kr-video-slider').forEach((root) => sliders.get(root)?.()));
})();
